import type { Territory } from '@amzn/vinyl-amzn'
import type { Maybe } from '@amazon/vinyl-util'
import { getLocale } from '@amzn/bf-i18n'
import marketplaceTable from '@/marketplaces.json'

// Green Hornet's region groups (DomainConstants.ts); GH's `UK` market is the ISO code `GB`.
export const NA_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>(['US', 'CA', 'MX', 'BR'])

export const ROW_NA_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'AR',
    'CL',
    'CO',
    'BO',
    'CR',
    'DO',
    'EC',
    'SV',
    'GT',
    'HN',
    'NI',
    'PA',
    'PY',
    'PE',
    'UY',
])

export const EU_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'GB',
    'DE',
    'FR',
    'IT',
    'ES',
    'IN',
])

export const ROE_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'BE',
    'BG',
    'CY',
    'CZ',
    'EE',
    'FI',
    'GR',
    'HU',
    'IE',
    'IS',
    'LV',
    'LI',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'SK',
    'SE',
])

export const FE_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>(['JP', 'AU', 'NZ'])

// Middle East music territories GH has no market for; its weblabs allocate them as AV_ROW_EU.
export const ROW_EU_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>(['AE', 'EG', 'SA'])

export const CLASSIC_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    'US',
    'GB',
    'DE',
    'FR',
    'IT',
    'ES',
    'JP',
])

export const KNOWN_TERRITORIES: ReadonlySet<Territory> = new Set<Territory>([
    ...NA_TERRITORIES,
    ...ROW_NA_TERRITORIES,
    ...EU_TERRITORIES,
    ...ROE_TERRITORIES,
    ...FE_TERRITORIES,
    ...ROW_EU_TERRITORIES,
    'CH',
])

/**
 * Podcasts are Alexa-only in every ROE territory except Ireland, so GH offers no podcast surface
 * there. Keyed on ROE membership, so a territory added to ROE later is excluded by default.
 */
export function arePodcastsAvailable(territory: Maybe<string>): boolean {
    if (territory === 'IE') return true
    return !(territory != null && (ROE_TERRITORIES as ReadonlySet<string>).has(territory))
}

/** The Activity feed is off across every launching ROW territory (ROW-NA and ROE); GH's `isActivityFeedAvailable()`. */
export function isActivityFeedAvailable(territory: Maybe<string>): boolean {
    if (territory == null) return true
    return !(
        (ROW_NA_TERRITORIES as ReadonlySet<string>).has(territory) ||
        (ROE_TERRITORIES as ReadonlySet<string>).has(territory)
    )
}

export function isKnownTerritory(code: Maybe<string>): code is Territory {
    return code != null && (KNOWN_TERRITORIES as ReadonlySet<string>).has(code)
}

// Amazon Music serves Austrian accounts as the DE music territory.
const TERRITORY_ALIASES: Readonly<Partial<Record<string, Territory>>> = { AT: 'DE' }

/** The music territory an account's market code stands for, undefined when Bullfrog does not know it. */
export function toMusicTerritory(code: Maybe<string>): Territory | undefined {
    const territory = code == null ? code : (TERRITORY_ALIASES[code] ?? code)
    return isKnownTerritory(territory) ? territory : undefined
}

/** A territory with a retail site of its own. */
export type RetailTerritory =
    | 'US'
    | 'CA'
    | 'BR'
    | 'MX'
    | 'GB'
    | 'DE'
    | 'FR'
    | 'IT'
    | 'ES'
    | 'IN'
    | 'JP'
    | 'AU'

/**
 * A retail marketplace: its amazon TLD, music territory and marketplace id, and the Auth Portal
 * association handle the desktop app signs in with. The desktop shell reads the same table.
 */
export interface Marketplace {
    readonly tld: string
    readonly territory: RetailTerritory
    readonly marketplaceId: string
    readonly assocHandle: string
}

export const MARKETPLACES = marketplaceTable as readonly Marketplace[]

const TLD_TERRITORY: Readonly<Record<string, RetailTerritory>> = Object.fromEntries(
    MARKETPLACES.map((m) => [m.tld, m.territory])
)

const TERRITORY_TLD = Object.fromEntries(MARKETPLACES.map((m) => [m.territory, m.tld])) as Record<
    RetailTerritory,
    string
>

/** Every TLD an `amazon.<tld>` marketplace is served on, e.g. `com`, `co.uk`, `co.jp`. */
export const MARKETPLACE_TLDS: readonly string[] = MARKETPLACES.map((m) => m.tld)

const RETAIL_TERRITORY_OVERRIDES: Partial<Record<Territory, RetailTerritory>> = { NZ: 'AU' }

// A non-amazon host (localhost, *.amazon.dev) has no match and resolves to US.
const TLD_MATCH = new RegExp(
    `\\.amazon\\.(${Object.keys(TLD_TERRITORY)
        .map((tld) => tld.replaceAll('.', '\\.'))
        .join('|')})$`
)

export function territoryFromHostname(hostname: string): Territory {
    return TLD_TERRITORY[hostname.match(TLD_MATCH)?.[1] ?? 'com']
}

/** The territory the page's amazon TLD serves; US off-web, where there is no `location`. */
export function hostTerritory(): Territory {
    return typeof location !== 'undefined' ? territoryFromHostname(location.hostname) : 'US'
}

function isRetailTerritory(territory: Territory): territory is RetailTerritory {
    return territory in TERRITORY_TLD
}

/**
 * The territory whose retail site serves `territory`: its own where it has one,
 * amazon.com.au for NZ, and amazon.com for every other territory.
 */
export function retailTerritory(territory: Territory): RetailTerritory {
    const retail = RETAIL_TERRITORY_OVERRIDES[territory] ?? territory
    return isRetailTerritory(retail) ? retail : 'US'
}

/** A marketplace GH's weblabs allocate by: a retail site, or a Prime Video rest-of-world one. */
export type WeblabMarketplace = RetailTerritory | 'AV_ROW_NA' | 'AV_ROE' | 'AV_ROW_EU' | 'AV_ROW_FE'

/** The weblab marketplace serving `territory`: its rest-of-world one, else its retail site's. */
export function weblabMarketplace(territory: Territory): WeblabMarketplace {
    if (ROW_NA_TERRITORIES.has(territory)) return 'AV_ROW_NA'
    if (ROE_TERRITORIES.has(territory)) return 'AV_ROE'
    if (ROW_EU_TERRITORIES.has(territory)) return 'AV_ROW_EU'
    if (territory === 'NZ') return 'AV_ROW_FE'
    // Amazon Music serves Swiss accounts from the DE marketplace.
    if (territory === 'CH') return 'DE'
    return retailTerritory(territory)
}

/** The service region (`NA`, `EU` or `FE`) that serves `territory`, mirroring GH's getMarketplaceRegion(). */
export function marketplaceRegion(territory: Territory): 'NA' | 'EU' | 'FE' {
    if (EU_TERRITORIES.has(territory) || ROE_TERRITORIES.has(territory)) return 'EU'
    if (FE_TERRITORIES.has(territory)) return 'FE'
    return 'NA'
}

/**
 * The territory's retail host (`www.amazon.co.uk` for GB), mirroring GH's getRetailDomain(): the
 * rest-of-Europe territories have no retail site, and amazon.co.uk is the nearest. The music host
 * and the marketplace id keep following `retailTerritory`.
 */
export function retailDomain(territory: Territory): string {
    const site = ROE_TERRITORIES.has(territory) ? 'GB' : retailTerritory(territory)
    return `www.amazon.${TERRITORY_TLD[site]}`
}

/** The TLD of the territory's Amazon Music web host (`co.uk` for GB). */
export function musicTld(territory: Territory): string {
    return TERRITORY_TLD[retailTerritory(territory)]
}

/** A page on the territory's Amazon Music web host (`music.amazon.co.uk` for GB), mirroring GH's getMusicBaseUrl(). */
export function musicUrl(territory: Territory, path: string): string {
    return new URL(path, `https://music.amazon.${musicTld(territory)}`).toString()
}

/** A page on the territory's retail site, carrying the UI locale the way GH's cookie links do. */
export function retailUrl(territory: Territory, path: string): string {
    const url = new URL(path, `https://${retailDomain(territory)}`)
    url.searchParams.append('locale', getLocale().value.replace('-', '_'))
    return url.toString()
}

export function termsOfUseUrl(territory: Territory): string {
    return `https://${retailDomain(territory)}/gp/help/customer/display.html?pop-up=1&nodeId=201380010`
}

export function customerServiceUrl(territory: Territory): string {
    return `https://${retailDomain(territory)}/gp/help/customer/contact-us/?ref=cu_surl`
}

export function helpFaqUrl(territory: Territory): string {
    return `https://${retailDomain(territory)}/gp/help/customer/display.html?nodeId=G5ZL5HPKATHSNWKP`
}

export function audioQualityHelpUrl(territory: Territory): string {
    return `https://${retailDomain(territory)}/gp/help/customer/display.html?nodeId=GN6KRDWLKJ6FR9LJ`
}

export function cookiePreferencesUrl(territory: Territory): string {
    return retailUrl(territory, '/privacyprefs/music')
}

/** Amazon's page of the devices registered to the account, which Morpho opens for device authorization. */
export function deviceManagementUrl(territory: Territory): string {
    return retailUrl(territory, '/gp/dmusic/device/manage')
}

export function musicPlanSettingsUrl(territory: Territory): string {
    return `https://${retailDomain(territory)}/music/settings?ref=dp_amp_settings_yasettings_click`
}

const STAGE_REGIONS = [
    '',
    'ca-',
    'mx-',
    'br-',
    'uk-',
    'de-',
    'fr-',
    'it-',
    'es-',
    'in-',
    'jp-',
    'au-',
]

// GH's isAmazonDomain hosts: every stage of the music and retail web hosts, plus the app-install host.
const AMAZON_HOSTS: ReadonlySet<string> = new Set([
    ...Object.keys(TLD_TERRITORY).flatMap((tld) =>
        ['www', 'music', 'gamma.music', 'dev.music', 'integ.music', 'local.music'].map(
            (prefix) => `${prefix}.amazon.${tld}`
        )
    ),
    ...STAGE_REGIONS.flatMap((region) => [
        `${region}pre-prod.amazon.com`,
        `${region}development.amazon.com`,
        `music${region ? `-${region.slice(0, -1)}` : ''}.integ.amazon.com`,
    ]),
    'musicapp.amazon.com',
    'music-gamma.amazon.com',
    'music-ca-iad.iad.proxy.amazon.com',
    'music-mx-iad.iad.proxy.amazon.com',
    'music-br-preprod-iad.iad.proxy.amazon.com',
    'music-uk-dub.dub.proxy.amazon.com',
    'music-de-dub.dub.proxy.amazon.com',
    'music-fr-dub.dub.proxy.amazon.com',
    'music-it-dub.dub.proxy.amazon.com',
    'music-es-dub.dub.proxy.amazon.com',
    'music-in-dub.dub.proxy.amazon.com',
    'music-jp-pdx.pdx.proxy.amazon.com',
    'music-au-pdx.pdx.proxy.amazon.com',
])

/** Whether `link` is an absolute URL on an Amazon Music or Amazon retail host; false when it doesn't parse. */
export function isAmazonDomain(link: string): boolean {
    try {
        return AMAZON_HOSTS.has(new URL(link).hostname)
    } catch {
        return false
    }
}
